import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

/* App-wide banner for managers and admin: probation reviews that are due THIS WEEK or already
   overdue (Salem 2026-08-10).

   Two deliberate choices:
   - it stays up until the review is actually SUBMITTED — the list comes from the server, so
     dismissing only hides it until tomorrow; there is no way to make it go away without doing
     the review (or the review being done by someone else);
   - every manager sees every outstanding review, not just their own team, because the point is
     that it gets done by whoever is free.
   Anyone who isn't a manager/admin, and any week with nothing due, renders nothing at all. */

const DISMISS_KEY = "repiq_probation_banner_dismissed";

export default function ProbationBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === new Date().toISOString().slice(0, 10); }
    catch { return false; }
  });

  useEffect(() => {
    let live = true;
    const load = () => api.get("/api/v1/hr/probation/due")
      .then((r) => { if (live) setData(r); }).catch(() => {});
    load();
    // Re-check every 10 minutes so the banner clears itself shortly after someone submits.
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { live = false; clearInterval(t); };
  }, []);

  const reviews = data?.reviews || [];
  if (!data?.canConduct || reviews.length === 0 || hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch { /* ignore */ }
    setHidden(true);
  };
  const mine = reviews.filter((r) => r.yours);
  const others = reviews.filter((r) => !r.yours);
  const overdue = (mine.length ? mine : reviews).filter((r) => r.overdueDays > 0);
  const tone = overdue.length ? "var(--red)" : mine.length ? "var(--amber)" : "var(--text-faint)";
  const first = mine[0];

  return (
    <div style={{
      background: `color-mix(in srgb, ${tone} 12%, var(--surface))`,
      borderBottom: `2px solid ${tone}`, padding: "8px 16px",
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12.5,
    }}>
      <span style={{ fontSize: 16 }}>{overdue.length ? "🔴" : "📋"}</span>
      <span style={{ flex: 1, minWidth: 260 }}>
        {first ? (
          <>
            <b>{first.name}</b>'s {first.label.toLowerCase()} is{" "}
            {first.overdueDays > 0
              ? <b style={{ color: tone }}>{first.overdueDays} day{first.overdueDays === 1 ? "" : "s"} overdue</b>
              : <>due <b>{first.dueLabel}</b></>}
            {mine.length > 1 && (
              <span className="muted"> · and {mine.length - 1} more of yours this week
                {" "}({mine.slice(1).map((r) => r.name).join(", ")})</span>
            )}
            <span className="muted"> — RepIQ drafts the report from their real record; you edit and submit.</span>
          </>
        ) : (
          <span className="muted">
            {others.length} probation review{others.length === 1 ? "" : "s"} due this week on other teams
            {" "}({others.map((r) => r.name).join(", ")}) — their manager or an admin conducts them.
          </span>
        )}
        {first && others.length > 0 && (
          <span className="muted"> Also due elsewhere: {others.map((r) => r.name).join(", ")}.</span>
        )}
      </span>
      {first && (
        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/people/${first.userId}`)}>
          Conduct {first.name.split(" ")[0]}'s review
        </button>
      )}
      {(others.length > 0 || mine.length > 1) && (
        <button className="btn btn-outline btn-sm" onClick={() => navigate("/people")}>See all</button>
      )}
      <button className="btn btn-ghost btn-sm" title="Hides until tomorrow — it comes back until the review is submitted"
        onClick={dismiss}>✕</button>
    </div>
  );
}
